import { inject, Injectable, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router
} from '@angular/router';
import { BehaviorSubject, of, Subject, timer } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LoadingPageService {
  private _loadingPath = signal(false);
  public readonly loadingPath = this._loadingPath.asReadonly();

  private router = inject(Router);

  private navigationCompleteSubject = new BehaviorSubject<string>(
    this.router.url
  );
  public readonly navigationEnd$ =
    this.navigationCompleteSubject.asObservable();

  private stopSubject = new Subject<void>();

  constructor() {
    this.handleRouterEvents();
  }

  /**
   * Manually stop the loading skeleton
   */
  public stopLoader(): void {
    this._loadingPath.set(false);
    this.stopSubject.next();
  }

  /**
   * Resets the loader state to hidden.
   */
  public resetLoader(): void {
    this.stopLoader();
  }

  public navigate(route: string): void {
    this.router.navigate([route]);
  }

  /**
   * Handle navigation loader by listening to router events.
   */
  private handleRouterEvents(): void {
    const navigationStart$ = this.router.events.pipe(
      filter((e): e is NavigationStart => e instanceof NavigationStart)
    );

    const navigationComplete$ = this.router.events.pipe(
      filter(
        (e) =>
          e instanceof NavigationEnd ||
          e instanceof NavigationCancel ||
          e instanceof NavigationError
      )
    );

    navigationStart$
      .pipe(
        switchMap((startEvent: NavigationStart) => {
          // Compare paths without query params or fragments
          const currentPath = this.router.url.split(/[?#]/)[0];
          const targetPath = startEvent.url.split(/[?#]/)[0];

          if (currentPath !== targetPath) {
            // Start a 300ms timer that will be cancelled if navigationComplete$ OR stopSubject emits
            return timer(300).pipe(
              takeUntil(navigationComplete$),
              takeUntil(this.stopSubject),
              tap(() => this._loadingPath.set(true))
            );
          }

          return of(null);
        })
      )
      .subscribe();

    // Always hide the loader once navigation finishes or fails
    navigationComplete$.subscribe((event) => {
      this._loadingPath.set(false);

      const url =
        event instanceof NavigationEnd ? event.urlAfterRedirects : event.url;

      this.navigationCompleteSubject.next(url);
    });
  }
}
